import CallWidgetsResource from 'flow/api/callwidgets';
import {
    appendBankIDAPPwithDirection,
    replaceCallInitiatorKeyAndNumber,
    BANKID_CALL_INITIATOR,
} from 'flow/utils/bankid-call-widget-utils';
import Actions from 'flow/actions';

import {
    getSortedExternalSearchCallWidgetUrlsWithPhoneNumbers,
    getVersion2Widgets,
    populateUrl,
    validateProtocol,
} from 'flow/utils/run-url-utils';
import {
    getFocusedActiveCall,
    getOwnActiveCalls,
    selectOwnActiveLineDataWithKey,
} from 'flow/selectors/call-selectors';
import {
    getOwnExtensionKey,
    getQueueNumberFromQueueId,
} from 'flow/selectors/selectors';
import { httpGetErrorDispatcher, networkErrorDispatcher } from './app-actions';
import { HTTPStatusCodes } from 'flow/utils';
import { getCallWidgetVariables } from 'flow/selectors/call-widget-selectors';
import { TRIGGER_TYPE } from 'flow/utils/index';

export const tryAutoOpenWidgetUrls = (activeCall) => (dispatch, getState) => {
    dispatch(tryAutoOpenExternalSearchUrls()); //Widget 1.0
    const callWidgetsVersion2 = getVersion2Widgets(getState().callWidgets);
    if (callWidgetsVersion2 && callWidgetsVersion2.size) {
        dispatch(
            tryAutoOpenExternalContactUrl(activeCall, callWidgetsVersion2),
        );
    }
};

export const tryAutoOpenExternalContactUrl =
    (activeCall, callWidgets) => (dispatch, getState) => {
        //make sure the callwidget contain url and trigger type is "onanswer"
        callWidgets = callWidgets.filter((callWidget) => {
            return (
                callWidget.get('url', null) &&
                callWidget.get('triggerType', '') === TRIGGER_TYPE.ON_ANSWER
            );
        });

        if (callWidgets.size) {
            const state = getState();
            const ownActiveCalls = getOwnActiveCalls(state);
            const ownActiveCall = ownActiveCalls.find(
                (ownActiveCall) => ownActiveCall.get('key') === activeCall.key,
            );
            const queueId = ownActiveCall ? ownActiveCall.get('queue') : null;
            const queueNumber = getQueueNumberFromQueueId(state, queueId);
            const variables = getCallWidgetVariables(
                state,
                ownActiveCall,
                queueNumber,
            );
            if (variables.CONTACT) {
                const urls = callWidgets
                    .map((callWidget) =>
                        populateUrl(callWidget.get('url', null), variables),
                    )
                    .toArray();
                openNewCallWidgetLinks(
                    dispatch,
                    getState,
                    activeCall.key,
                    urls,
                );
            }
        }
    };

export const tryAutoOpenExternalSearchUrls = () => (dispatch, getState) => {
    const state = getState();
    const callWidgets = state.callWidgets;
    const focusedActiveCall = getFocusedActiveCall(state);
    if (focusedActiveCall) {
        const queueId = focusedActiveCall.get('queue');
        const queueNumber = getQueueNumberFromQueueId(state, queueId);

        const variables = getCallWidgetVariables(
            state,
            focusedActiveCall,
            queueNumber,
        );

        const urls = getSortedExternalSearchCallWidgetUrlsWithPhoneNumbers(
            callWidgets,
            variables,
        );

        const onAnswerUrls = urls
            .filter(({ triggerType }) => triggerType === TRIGGER_TYPE.ON_ANSWER)
            .map(({ url }) => url);
        openNewCallWidgetLinks(
            dispatch,
            getState,
            focusedActiveCall.get('key'),
            onAnswerUrls,
        );
    }
};

// The answer ('NewState Up') and each 'QueueCallInfo' can auto-open the call
// widgets of the same call, and Top sends one 'QueueCallInfo' per queue for a
// picked call. Opening a URL again makes a CRM such as Carisma create the same
// case twice, so a call keeps the URLs it has opened and only opens the others.
// A later event that changes a URL, such as 'originalRdnis' filling in
// 'C-NUMBER', still opens it.
const openNewCallWidgetLinks = (dispatch, getState, callKey, urls) => {
    const call = selectOwnActiveLineDataWithKey(getState(), callKey);
    const openedUrls = call?.get('autoOpenedCallWidgetUrls')?.toArray() ?? [];
    const newUrls = urls.filter((url) => url && !openedUrls.includes(url));

    newUrls.forEach(openCallWidgetLink);

    // Only for one of our calls: updating a key the store doesn't have would
    // add a call with nothing but that key.
    if (call && newUrls.length) {
        dispatch(
            receiveAutoOpenedCallWidgetUrls(
                getOwnExtensionKey(getState()),
                callKey,
                [...openedUrls, ...newUrls],
            ),
        );
    }
};

export const openCallWidgetLink = (url) => {
    if (!url) {
        return;
    }

    if (url.includes(BANKID_CALL_INITIATOR)) {
        const newURL = replaceCallInitiatorKeyAndNumber(url);
        if (newURL) {
            url = newURL;
        }
    }

    const hasProtocol = validateProtocol(url);
    const urlWithProtocol = hasProtocol ? url : `https://${url}`;

    window.open(urlWithProtocol, '_blank');
};

export const fetchCallWidgets = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        CallWidgetsResource.getCallWidgets({
            onResult: (res) => {
                res = appendBankIDAPPwithDirection(res);
                dispatch(receiveCallWidgets(res));
                resolve(res);
            },
            onError: (err) => {
                if (err.status === HTTPStatusCodes.FORBIDDEN_403) {
                    dispatch(receiveCallWidgets(null));
                    resolve(null);
                } else {
                    httpGetErrorDispatcher(dispatch);
                    reject(err);
                }
            },
        });
    });
};

export const updateCallWidget = (callWidget) => (dispatch) => {
    CallWidgetsResource.updateCallWidget(
        {
            onResult: (res) => {
                dispatch(receiveUpdateCallWidget(res));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        callWidget,
    );
};

export const deleteCallWidget = (callWidgetKey) => (dispatch) => {
    CallWidgetsResource.deleteCallWidget(
        {
            onResult: () => {
                dispatch(receiveDeleteCallWidget(callWidgetKey));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        callWidgetKey,
    );
};

const receiveAutoOpenedCallWidgetUrls = (extensionKey, callKey, urls) => ({
    type: Actions.EXTENSION_UPDATE_ACTIVE_CALL,
    extensionKey,
    data: { key: callKey, autoOpenedCallWidgetUrls: urls },
});

const receiveCallWidgets = (callWidgets) => ({
    type: Actions.RESULT_GET_CALL_WIDGETS,
    callWidgets: callWidgets,
});

const receiveUpdateCallWidget = (callWidget) => ({
    type: Actions.RESULT_UPDATE_CALL_WIDGET,
    callWidget: callWidget,
});

const receiveDeleteCallWidget = (deletedCallWidgetKey) => ({
    type: Actions.RESULT_DELETE_CALL_WIDGET,
    callWidgetKey: deletedCallWidgetKey,
});

export const receiveUpdateCallWidgetTriggerType = (
    callWidgetKey,
    triggerType,
) => ({
    type: Actions.UPDATE_CALL_WIDGET_TRIGGER_TYPE,
    callWidgetKey,
    triggerType,
});
